import { taggingEventsNC } from 'common-services/event-config';
import { useStoreService } from 'common-services/store/store-provider';
import React, { FC, PropsWithChildren, useContext, useCallback, useEffect, useState } from 'react';
import { CommonEventManager, IEvents } from '../event-manager';

export interface IEventManagerStoreTO {
  getTaggingData: () => Record<string, string | number>;
  getMarketConfig: () => TimApp.MarketConfiguration;
  ELEMENT_TYPE: string;
}

interface IEventManagerService {
  publishBusinessEvent: (events: Array<IEvents>, appSelector?: HTMLElement | null) => boolean;
  publishTaggingEvents: (events: Array<IEvents>, appSelector?: HTMLElement | null) => boolean;
  setEventManager: (eventManager: CommonEventManager) => void;
}

export type MarketType = 'USED_CARS' | 'NEW_CARS';

export const useEventManager = (): IEventManagerService => {
  const marketMap = {
    usedCarsSimulator: 'USED_CARS',
    newCarsSimulator: 'NEW_CARS',
  };
  const { state, getTaggingData, getMarketConfig } = useStoreService();
  const [eventManager, setEventManager] = useState<CommonEventManager>(
    new CommonEventManager('', null, [], taggingEventsNC)
  );

  useEffect(() => {
    eventManager.setStoreContext(
      {
        ELEMENT_TYPE: marketMap[state.marketConfig.elementType],
        getTaggingData,
        getMarketConfig,
      },
      state.appSelector
    );
  }, [state.tagging]);

  useEffect(() => {
    if (eventManager) {
      eventManager.init();
    }
  }, [eventManager]);

  const handlePublishTaggingEvent = useCallback(
    () => eventManager?.publishTaggingEvents,
    [eventManager.publishTaggingEvents]
  );

  const handlePublishBusinessEvent = useCallback(
    () => eventManager?.publishBusinessEvent,
    [eventManager.publishTaggingEvents]
  );

  return {
    publishBusinessEvent: handlePublishBusinessEvent(),
    publishTaggingEvents: handlePublishTaggingEvent(),
    setEventManager,
  };
};

const EventManagerContext = React.createContext<IEventManagerService>({} as IEventManagerService);

export const EventManagerProvider: FC<PropsWithChildren<{}>> = (props): JSX.Element => {
  return <EventManagerContext.Provider value={useEventManager()}>{props.children}</EventManagerContext.Provider>;
};

export const useEventManagerService = (): IEventManagerService => useContext(EventManagerContext);
